import { useState } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useNavigationLinks from "@hooks/useNavigationLinks"
import useRouter from "@hooks/useRouter"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import extractURLSearchParam from "@utils/extractURLSearchParam"
import filterUserLinks from "@utils/filterUserLinks"

import { AccessCheck } from "@particles"
import { usePreviewMode } from "@particles/PreviewMode/PreviewMode.context"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { JOBS_ROUTES } from "@routes/jobs"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    JOBS_LIST_CONTROLS_ACTIVE_TAB,
    JOBS_LIST_CONTROLS_KEYWORD_SEARCH,
    JOBS_LIST_CONTROLS_PHASE,
    JOBS_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import { JOB_CARDS_VIEW_ATTRIBUTE_OPTIONS, JOB_CARDS_VIEW_SORT_OPTIONS } from "./JobList.constants"
import styles from "./JobList.module.scss"
import { JobObjectsViewTabs } from "./JobObjectsViewTabs"
import JobCardsView from "./views/JobCardsView/JobCardsView"
import JobTableView from "./views/JobTableView/JobTableView"
import JobPreviewCard from "./views/JobTimelineView/JobPreviewCard/JobPreviewCard"
import JobTimelineView from "./views/JobTimelineView/JobTimelineView"

function JobListContent() {
    const { user } = useUser()
    const isDesktop = useIsDesktop()
    const [createRoute] = useSlugExtractor([JOBS_ROUTES.CREATE])
    const { hasPermission } = useHasAccess()

    const seriesFilter = extractURLSearchParam("filter__series", false)
    const seriesSearch = new URLSearchParams()

    if (seriesFilter) {
        seriesSearch.set("series", seriesFilter)
    }

    const [hoveredJobId, setHoveredJobId] = useState<Job["id"]>("")
    const { isPreviewModeEnabled } = usePreviewMode()

    const router = useRouter()
    const { jobs: jobsLink } = useNavigationLinks()

    const sublinks = filterUserLinks(jobsLink.subLinks, user)

    return (
        <>
            <ViewTypesProvider
                viewTypes={["table", "cards", "timeline"]}
                viewTypeKey={JOBS_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle
                                        label={objectMap.job.labelPlural}
                                        navigationItems={
                                            sublinks.length > 1
                                                ? sublinks.map((item) => ({
                                                      label: item.label,
                                                      value: item.path,
                                                      isActive: item.label === "Jobs",
                                                  }))
                                                : undefined
                                        }
                                        onNavigationItemSelect={(item) => {
                                            router.push(item.value)
                                        }}
                                    />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            jobs_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                jobs_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Jobs" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            jobs_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        {activeViewType === "timeline" && <JobTimelineView />}
                        {activeViewType !== "timeline" && (
                            <div className={styles.base} onMouseLeave={() => setHoveredJobId("")}>
                                <ObjectsView.Provider
                                    activePhaseStorageKey={JOBS_LIST_CONTROLS_PHASE}
                                    activeTabStorageKey={JOBS_LIST_CONTROLS_ACTIVE_TAB}
                                    searchKeywordsStorageKey={JOBS_LIST_CONTROLS_KEYWORD_SEARCH}
                                    listTitle="Jobs"
                                    listDataFilters={seriesSearch}
                                    objectType="job"
                                    objectCreateRoute={createRoute}
                                    objectIndexName="Job"
                                    objectName="Job"
                                    defaultActivePhase="open"
                                    isRowClickable={hasPermission("jobs_view_permission", PERMISSION_LEVEL.FULL)}
                                    isEmptyStateCreateButtonVisible={hasPermission(
                                        "jobs_create_permission",
                                        PERMISSION_LEVEL.RESTRICTED,
                                    )}
                                    onRowHoverChange={setHoveredJobId}
                                >
                                    <TableView.Provider>
                                        <CardsView.Provider
                                            attributes={JOB_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                            sortOptions={JOB_CARDS_VIEW_SORT_OPTIONS}
                                        >
                                            <div onMouseEnter={() => setHoveredJobId("")}>
                                                <ViewBar.Root>
                                                    <ViewBar.Start>
                                                        <ViewBar.Phases>
                                                            <ObjectsView.Phase phase="open" title="Open" />
                                                            <ObjectsView.Phase phase="closed" title="Closed" />
                                                        </ViewBar.Phases>
                                                        <ViewBar.VerticalDivider />
                                                        <JobObjectsViewTabs />
                                                    </ViewBar.Start>
                                                    <ViewBar.End>
                                                        <ObjectsView.SearchInput />
                                                        <ViewMenu />
                                                    </ViewBar.End>
                                                </ViewBar.Root>
                                            </div>
                                            {activeViewType === "table" && <JobTableView />}
                                            {activeViewType === "cards" && <JobCardsView />}
                                        </CardsView.Provider>
                                    </TableView.Provider>
                                </ObjectsView.Provider>
                                <JobPreviewCard
                                    jobId={hoveredJobId || ""}
                                    isVisible={!!hoveredJobId && isPreviewModeEnabled}
                                    isPositionFixed={true}
                                />
                            </div>
                        )}
                    </>
                )}
            </ViewTypesProvider>
        </>
    )
}

export default function JobList() {
    return (
        <AppFrame.Root collaborationRoomName="job-list">
            <JobListContent />
        </AppFrame.Root>
    )
}
